import { inject, Injectable } from '@angular/core';
import {
	GetWorkOrderQuery,
	WorkOrderCalendarQuery,
	WorkOrdersQuery,
} from '@app/pages/cmms/graphql/work-order.queries';
import { map, pluck, take } from 'rxjs/operators';
import { Apollo } from 'apollo-angular';
import {
	CopyWorkOrderMutation,
	CreateWorkOrderMutation,
	DeleteChecklistMutation,
	DeleteWorkOrderMutation,
	DeleteWorkOrdersMutation,
	EditWorkOrderMutation,
	UnlinkWorkOrderObjects,
	UpdateChecklistMutation,
} from '@app/pages/cmms/graphql/work-order.mutations';
import { IAuPaginated } from '@shared/ui-components/au-paginator/pagination-page.info';
import {
	setWorkOrderForEditing,
	storeWorkOrdersQueryVariables,
} from '@app/pages/cmms/components/work-order/state/work-order.actions';
import { Store } from '@ngrx/store';
import { IWorkOrderState } from '@app/pages/cmms/components/work-order/state/work-order.reducer';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { NewWorkOrderComponent } from '@app/pages/cmms/components/work-order/new-work-order/new-work-order.component';
import { iconEnum } from '@app/configs/model/IconEnum';
import { Observable, Subject } from 'rxjs';
import { AuErrorAdapter } from '@app/shared';
import { IconSnackBarService } from '@app/core/services/icon-snack-bar.service';
import { NewRightSidebarService } from '@app/layout/new-right-sidebar/services/new-right-sidebar/new-right-sidebar.service';
import {
	OpenSidebarEventTypes,
	OpenWorkOrder,
	RightSidebarRoutes,
} from '@app/layout/new-right-sidebar/types';
import { ACTIVE_PAGE_CONFIG } from '../configs/active-page.config';
import { CmmsPages } from '../state/cmms.reducer';
import { FeatureFlagService } from '@app/core/services/feature-flag.service';
import {
	ICalendarWorkOrderQueryResponse,
	IWorkOrder,
	IWorkOrderEditPayload,
	IWorkOrdersPaginatedData,
} from '@app/domain/cmms-types/workorder-types';
import {
	ICmmsWorkOrderQueryVariables,
	IWorkOrderCopyVariables,
	IWorkOrderCreateResponse,
	IWorkOrderUpdateSuccess,
} from '@app/domain/gql/types';

@Injectable({
	providedIn: 'root',
})
export class WorkOrdersService {
	private readonly newSidebarService = inject(NewRightSidebarService);
	private readonly featureFlagsService = inject(FeatureFlagService);

	updateCalendarData$ = new Subject<void>();

	constructor(
		private apollo: Apollo,
		private store$: Store<IWorkOrderState>,
		private sidebarService: AuRightSidebarService,
		private notify: IconSnackBarService
	) {}

	loadWorkOrders(variables: ICmmsWorkOrderQueryVariables) {
		// Due to filter and sort service are provided in component,
		// we need to store the query variables in store to use that in effects
		// when we update table after creating new item
		this.store$.dispatch(storeWorkOrdersQueryVariables({ variables }));

		return this.apollo
			.query<IWorkOrdersPaginatedData>({
				query: WorkOrdersQuery,
				fetchPolicy: 'network-only',
				variables,
			})
			.pipe(
				pluck('data', 'workOrders'),
				map(data => this.getLoadedWorkOrderData(data))
			);
	}

	loadCalendarWorkOrders(variables: ICmmsWorkOrderQueryVariables) {
		return this.apollo
			.query<ICalendarWorkOrderQueryResponse>({
				query: WorkOrderCalendarQuery,
				fetchPolicy: 'network-only',
				variables: {
					...variables,
					isCalendarQuery: true,
				},
			})
			.pipe(
				pluck('data'),
				map(data => {
					return {
						workOrders: data.workOrders?.edges.map(edge => edge.node),
						futureRoutines: data.futureRoutines?.edges.map(edge => edge.node),
					};
				})
			);
	}

	getLoadedWorkOrderData(paginatedWorkOrders: IAuPaginated<IWorkOrder>) {
		const workOrders = paginatedWorkOrders.edges.map(edge => edge.node);

		return {
			workOrders,
			workOrderPagination: {
				pageInfo: paginatedWorkOrders.pageInfo,
				totalCount: paginatedWorkOrders.totalCount,
			},
		};
	}

	createWorkOrder(variables: IWorkOrderEditPayload) {
		return this.apollo
			.mutate<IWorkOrderCreateResponse>({
				mutation: CreateWorkOrderMutation,
				variables,
			})
			.pipe(map(response => response?.data?.createWorkOrder));
	}

	deleteWorkOrder(pk: string) {
		return this.apollo.mutate({
			mutation: DeleteWorkOrderMutation,
			fetchPolicy: 'network-only',
			variables: {
				pk,
			},
		});
	}

	deleteWorkOrders(pks: string[]) {
		return this.apollo.mutate({
			mutation: DeleteWorkOrdersMutation,
			fetchPolicy: 'network-only',
			variables: {
				workOrders: pks,
			},
		});
	}

	updateWorkOrder(
		workOrder: Partial<IWorkOrderEditPayload>,
		unlink?: {
			pk: string;
			incidents?: string;
			requests?: string;
			workOrders?: string;
		}[]
	) {
		unlink?.map(remove => {
			this.unlinkAttachedElements(remove).subscribe({
				error: error => {
					this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
				},
			});
		});

		return this.apollo.mutate<IWorkOrderUpdateSuccess>({
			mutation: EditWorkOrderMutation,
			fetchPolicy: 'network-only',
			variables: workOrder,
		});
	}

	getWorkOrderByPk(pk: string): Observable<IWorkOrder> {
		return this.apollo
			.query<{ workOrder: IWorkOrder }>({
				query: GetWorkOrderQuery,
				fetchPolicy: 'network-only',
				variables: {
					id: pk,
				},
			})
			.pipe(
				pluck('data', 'workOrder'),
				map(workOrder => {
					if (workOrder?.coordinates) {
						return {
							...workOrder,
							coordinates: {
								// removes a typeField from the object that graphql returns
								x: workOrder.coordinates.x,
								y: workOrder.coordinates.y,
								z: workOrder.coordinates.z,
							},
						};
					}
					return workOrder;
				})
			);
	}

	unlinkAttachedElements(variables: {
		pk: string;
		incidents?: string;
		requests?: string;
		workOrders?: string;
	}) {
		return this.apollo
			.mutate<{ success: boolean }>({
				mutation: UnlinkWorkOrderObjects,
				fetchPolicy: 'network-only',
				variables,
			})
			.pipe(take(1), pluck('data'));
	}

	unlinkAttachedElementLists(variables: {
		pk: string;
		incidents?: string[];
		requests?: string[];
		workOrders?: string[];
	}) {
		return this.apollo
			.mutate<{ success: boolean }>({
				mutation: UnlinkWorkOrderObjects,
				fetchPolicy: 'network-only',
				variables,
			})
			.pipe(
				take(1),
				map(({ data }) => data)
			);
	}

	viewWorkOrderInWorkspace(workOrder: IWorkOrder): void {
		if (this.featureFlagsService.isFFEnabled('new-sidebar')) {
			this.newSidebarService.openAndNavigate({
				pk: workOrder.pk,
				route: RightSidebarRoutes.WorkOrder,
				type: OpenSidebarEventTypes.OpenWorkOrder,
			} as OpenWorkOrder);
			return;
		}
		this.store$.dispatch(setWorkOrderForEditing({ workOrderEdit: workOrder }));

		this.sidebarService.setComponentAndOpen(
			ACTIVE_PAGE_CONFIG[CmmsPages.editWorkOrder].createComponent,
			{
				...ACTIVE_PAGE_CONFIG[CmmsPages.editWorkOrder].sidebarConfig,
				title: { text: 'workorder_id', endfix: workOrder.workOrderId },
			}
		);
	}

	openNewWorkOrderWorkspace(): void {
		this.sidebarService.setComponentAndOpen(NewWorkOrderComponent, {
			title: { text: 'new_work_order' },
			icon: iconEnum.cmms,
			actionBtnName: 'submit',
			showContentActions: true,
			propagateCloseEvent: true,
		});
	}

	deleteChecklist(checklistId: string): Observable<{ success: boolean }> {
		return this.apollo
			.mutate<{
				deleteChecklists: {
					success: boolean;
				};
			}>({
				mutation: DeleteChecklistMutation,
				variables: {
					ids: [checklistId],
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data.deleteChecklists)
			);
	}

	updateChecklist(
		checklistTemplate: string,
		checklistId: string
	): Observable<{ success: boolean }> {
		return this.apollo
			.mutate<{
				editChecklist: {
					success: boolean;
				};
			}>({
				mutation: UpdateChecklistMutation,
				variables: {
					pk: checklistId,
					template: checklistTemplate,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data.editChecklist)
			);
	}

	/**
	 * @param workOrders work orders pk
	 * @param metadata data that provide configuration of copy process
	 */
	copyWorkOrder(workOrders: string[], metadata?: IWorkOrderCopyVariables) {
		return this.apollo.mutate({
			mutation: CopyWorkOrderMutation,
			variables: {
				workOrders,
				...metadata,
			},
		});
	}
}
